import React, { useState, useEffect } from 'react';
import './SingleShipmentView.css';
import { API_BASE_URL } from '../config/api';

interface Shipment {
  id: number;
  name: string;
  status: string;
  created_at: string;
  items: ShipmentItem[];
}

interface ShipmentItem {
  id: number;
  name: string;
  required_quantity: number;
  actual_quantity?: number;
  is_completed: boolean;
  is_checked?: boolean;
  notes?: string;
  updated_at: string;
  batches: ShipmentBatch[];
}

interface ShipmentBatch {
  id: number;
  zoho_batch_id: string;
  zoho_batch_number: string;
  quantity_selected: number;
  storage_locations?: ShipmentBatchStorage[];
}

interface ShipmentBatchStorage {
  id: number;
  zoho_storage_id: string;
  storage_name: string;
  warehouse_id: string;
  warehouse_name: string;
  quantity: number;
  total_available_quantity?: number;
}

interface SingleShipmentViewProps {
  shipment: Shipment;
  onBack: () => void;
  onShipmentUpdate: (updatedShipment: Shipment) => void;
}

const SingleShipmentView: React.FC<SingleShipmentViewProps> = ({ shipment, onBack, onShipmentUpdate }) => {
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    actual_quantity: '',
    is_completed: false,
    is_checked: true,
    notes: ''
  });
  const [viewingNotesItem, setViewingNotesItem] = useState<number | null>(null);
  const [editingNotesItem, setEditingNotesItem] = useState<number | null>(null);
  const [notesEditForm, setNotesEditForm] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [processingShipment, setProcessingShipment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [shiftXCount, setShiftXCount] = useState(0);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey && event.key.toLowerCase() === 'x') {
        setShiftXCount(prev => {
          const newCount = prev + 1;
          if (newCount >= 15) {
            setDeleteEnabled(true);
            setTimeout(() => {
              setDeleteEnabled(false);
              setShiftXCount(0);
            }, 30000); 
            return 0;
          }
          return newCount;
        });
      } else {
        
        setShiftXCount(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const startEditingItem = (item: ShipmentItem) => {
    setEditingItem(item.id);
    setEditForm({
      actual_quantity: item.actual_quantity?.toString() || '',
      is_completed: item.is_completed,
      is_checked: item.is_checked ?? true,
      notes: item.notes || ''
    });
  };

  const cancelEditingItem = () => {
    setEditingItem(null);
    setEditForm({
      actual_quantity: '',
      is_completed: false,
      is_checked: true,
      notes: ''
    });
  };

  const saveItemEdit = async (shipmentId: number, itemId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          actual_quantity: editForm.actual_quantity ? parseInt(editForm.actual_quantity) : null,
          is_completed: editForm.is_completed,
          is_checked: editForm.is_checked,
          notes: editForm.notes
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update item');
      }

      
      const refreshResponse = await fetch(`${API_BASE_URL}/v1/shipments`);
      if (!refreshResponse.ok) {
        throw new Error('Failed to refresh shipments');
      }
      const allShipments = await refreshResponse.json();
      const updatedShipment = allShipments.find((s: Shipment) => s.id === shipmentId);
      if (updatedShipment) {
        onShipmentUpdate(updatedShipment);
      }

      setEditingItem(null);
      setEditForm({ actual_quantity: '', is_completed: false, is_checked: true, notes: '' });
    } catch (err: any) {
      setError(`Грешка при запазване: ${err.message}`);
    }
  };

  const viewItemNotes = async (shipmentId: number, itemId: number, currentlyViewing: boolean) => {
    if (!currentlyViewing) {
      
      try {
        await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/items/${itemId}/view-notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        
        const shipmentsResponse = await fetch(`${API_BASE_URL}/v1/shipments`);
        if (shipmentsResponse.ok) {
          const updatedShipments = await shipmentsResponse.json();
          const updatedShipment = updatedShipments.find((s: any) => s.id === shipmentId);
          if (updatedShipment) {
            onShipmentUpdate(updatedShipment);
          }
        }
      } catch (err) {
        console.error('Failed to auto-check item:', err);
      }
    }
    
    setViewingNotesItem(currentlyViewing ? null : itemId);
  };

  const startEditingNotes = (item: ShipmentItem) => {
    setEditingNotesItem(item.id);
    setNotesEditForm(item.notes || '');
    setViewingNotesItem(null); 
  };

  const cancelEditingNotes = () => {
    setEditingNotesItem(null);
    setNotesEditForm('');
  };

  const saveNotesEdit = async (shipmentId: number, itemId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: notesEditForm || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update notes');
      }

      
      const shipmentsResponse = await fetch(`${API_BASE_URL}/v1/shipments`);
      if (shipmentsResponse.ok) {
        const updatedShipments = await shipmentsResponse.json();
        const updatedShipment = updatedShipments.find((s: any) => s.id === shipmentId);
        if (updatedShipment) {
          onShipmentUpdate(updatedShipment);
        }
      }

      setEditingNotesItem(null);
      setNotesEditForm('');
    } catch (err: any) {
      setError(`Грешка при запазване на бележки: ${err.message}`);
    }
  };

  const removeItemFromShipment = async (shipmentId: number, itemId: number) => {
    if (!window.confirm('Сигурни ли сте, че искате да премахнете този артикул от пратката?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/items/${itemId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove item from shipment');
      }
      
      
      const refreshResponse = await fetch(`${API_BASE_URL}/v1/shipments`);
      if (!refreshResponse.ok) {
        throw new Error('Failed to refresh shipments');
      }
      const allShipments = await refreshResponse.json();
      const updatedShipment = allShipments.find((s: Shipment) => s.id === shipmentId);
      if (updatedShipment) {
        onShipmentUpdate(updatedShipment);
      }
    } catch (err: any) {
      setError(`Грешка при премахване: ${err.message}`);
    }
  };

  const canCompleteShipment = (shipment: Shipment) => {
    if (shipment.status === 'completed') return false;
    
    const hasCompletedItems = shipment.items.some(item => item.is_completed);
    return hasCompletedItems;
  };

  const completeShipment = async (shipmentId: number) => {
    setProcessingShipment(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/complete`, {
        method: 'PUT',
      });
      
      if (!response.ok) {
        throw new Error('Failed to complete shipment');
      }
      
      
      const refreshResponse = await fetch(`${API_BASE_URL}/v1/shipments`);
      if (!refreshResponse.ok) {
        throw new Error('Failed to refresh shipments');
      }
      const allShipments = await refreshResponse.json();
      const updatedShipment = allShipments.find((s: Shipment) => s.id === shipmentId);
      if (updatedShipment) {
        onShipmentUpdate(updatedShipment);
      }
    } catch (err: any) {
      setError(`Грешка при завършване на пратката: ${err.message}`);
    } finally {
      setProcessingShipment(false);
    }
  };

  const uncompleteShipment = async (shipmentId: number) => {
    setProcessingShipment(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/uncomplete`, {
        method: 'PUT',
      });
      
      if (!response.ok) {
        throw new Error('Failed to uncomplete shipment');
      }
      
      
      const refreshResponse = await fetch(`${API_BASE_URL}/v1/shipments`);
      if (!refreshResponse.ok) {
        throw new Error('Failed to refresh shipments');
      }
      const allShipments = await refreshResponse.json();
      const updatedShipment = allShipments.find((s: Shipment) => s.id === shipmentId);
      if (updatedShipment) {
        onShipmentUpdate(updatedShipment);
      }
    } catch (err: any) {
      setError(`Грешка при отмяна на завършването: ${err.message}`);
    } finally {
      setProcessingShipment(false);
    }
  };

  const checkUncheckedItems = async (shipmentId: number): Promise<{ hasUnchecked: boolean, uncheckedItems: any[] }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/unchecked-items`);
      if (!response.ok) {
        throw new Error('Failed to check unchecked items');
      }
      const data = await response.json();
      return { hasUnchecked: data.has_unchecked, uncheckedItems: data.unchecked_items };
    } catch (err) {
      console.error('Error checking unchecked items:', err);
      return { hasUnchecked: false, uncheckedItems: [] };
    }
  };

  const exportShipment = async (shipmentId: number, shipmentName: string) => {
    
    const { hasUnchecked, uncheckedItems } = await checkUncheckedItems(shipmentId);
    
    if (hasUnchecked) {
      const itemNames = uncheckedItems.map(item => item.item_name).join('\n• ');
      const proceed = window.confirm(
        `⚠️ Предупреждение!\n\nСледните артикули се нуждаят от ръчна проверка (бележките им не са прегледани):\n\n• ${itemNames}\n\nИскате ли да продължите с експорта?`
      );
      
      if (!proceed) {
        return; 
      }
    }
    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/export`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error('Failed to export shipment');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `${shipmentName}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(`Грешка при експортиране: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const exportForZoho = async (shipmentId: number, shipmentName: string) => {
    
    const { hasUnchecked, uncheckedItems } = await checkUncheckedItems(shipmentId);
    
    if (hasUnchecked) {
      const itemNames = uncheckedItems.map(item => item.item_name).join('\n• ');
      const proceed = window.confirm(
        `⚠️ Предупреждение!\n\nСледните артикули се нуждаят от ръчна проверка (бележките им не са прегледани):\n\n• ${itemNames}\n\nИскате ли да продължите с Zoho експорта?`
      );
      
      if (!proceed) {
        return; 
      }
    }

    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/shipments/${shipmentId}/export/zoho`);
      
      if (!response.ok) {
        throw new Error('Failed to export shipment for Zoho');
      }
      
      
      const now = new Date();
      const timestamp = now.toISOString().split('T')[0];
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const filename = `${shipmentName}_zoho_${timestamp}_${timeStr}.xlsx`;
      
      
      const blob = await response.blob();
      
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      setError(`Грешка при Zoho експорт: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusDisplayText = (status: string) => {
    return status === 'completed' ? 'Завършена' : 'В изпълнение';
  };

  return (
    <div className="single-shipment-view">
      <div className="single-shipment-header">
        <button className="back-button" onClick={onBack}>
          ← Назад към списъка
        </button>
        <h2>{shipment.name || `Пратка #${shipment.id}`}</h2>
        <div className="shipment-meta">
          <span>Създадена: {new Date(shipment.created_at).toLocaleString('bg-BG')}</span>
          <span className={`status ${shipment.status}`}>{getStatusDisplayText(shipment.status)}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="shipment-actions-bar">
        {canCompleteShipment(shipment) && (
          <button 
            className="complete-shipment-button"
            onClick={() => completeShipment(shipment.id)}
            disabled={processingShipment}
          >
            {processingShipment ? '⏳ Обработване...' : '✓ Завърши пратката'}
          </button>
        )}
        {shipment.status === 'completed' && (
          <button 
            className="uncomplete-shipment-button"
            onClick={() => uncompleteShipment(shipment.id)}
            disabled={processingShipment}
          >
            {processingShipment ? '⏳ Обработване...' : '↺ Отмени завършването'}
          </button>
        )}
        <button 
          className="export-shipment-button"
          onClick={() => exportShipment(shipment.id, shipment.name || `Пратка #${shipment.id}`)}
          disabled={isExporting}
        >
          {isExporting ? '↓ Експортиране...' : '⊞ Експорт в Excel'}
        </button>
        <button 
          className="export-shipment-button zoho-export"
          onClick={() => exportForZoho(shipment.id, shipment.name || `Пратка #${shipment.id}`)}
          disabled={isExporting}
        >
          {isExporting ? '↓ Експортиране...' : '⚙ Zoho експорт'}
        </button>
      </div>

      <div className="shipment-items-section">
        <h3>Артикули в пратката</h3>
        <table className="shipment-items-table">
          <thead>
            <tr>
              <th>Име на артикул</th>
              <th>Изисквано количество</th>
              <th>Действително количество</th>
              <th>Статус</th>
              <th>Проверен</th>
              <th>Избрани партиди</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {shipment.items.map((item) => (
              <React.Fragment key={item.id}>
                <tr className={item.is_checked === false ? 'unchecked-item' : ''}>
                  <td>
                    {item.is_checked === false && <span className="unchecked-indicator">⚠️ </span>}
                    {item.name}
                  </td>
                  <td>{item.required_quantity}</td>
                  <td>
                    {editingItem === item.id ? (
                      <input
                        type="number"
                        value={editForm.actual_quantity}
                        onChange={(e) => setEditForm({ ...editForm, actual_quantity: e.target.value })}
                        className="edit-input"
                      />
                    ) : (
                      item.actual_quantity || '-'
                    )}
                  </td>
                  <td>
                    {editingItem === item.id ? (
                      <label>
                        <input
                          type="checkbox"
                          checked={editForm.is_completed}
                          onChange={(e) => setEditForm({ ...editForm, is_completed: e.target.checked })}
                        />
                        Завършен
                      </label>
                    ) : (
                      <span className={`status ${item.is_completed ? 'completed' : 'pending'}`}>
                        {item.is_completed ? 'Завършен' : 'В изпълнение'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingItem === item.id ? (
                      <label>
                        <input
                          type="checkbox"
                          checked={editForm.is_checked}
                          onChange={(e) => setEditForm({ ...editForm, is_checked: e.target.checked })}
                        />
                        Проверен
                      </label>
                    ) : (
                      <span className={`status-badge ${item.is_checked !== false ? 'checked' : 'unchecked'}`}>
                        {item.is_checked !== false ? '✓' : '⚠️'}
                      </span>
                    )}
                  </td>
                  <td>
                    {item.batches.map((batch) => (
                      <div key={batch.id} className="batch-info">
                        <strong>{batch.zoho_batch_number}</strong>: {batch.quantity_selected}
                        {batch.storage_locations && batch.storage_locations.length > 0 && (
                          <div className="storage-locations">
                            {batch.storage_locations.map((storage) => (
                              <div key={storage.id} className="storage-location">
                                📍 {storage.storage_name} ({storage.warehouse_name}): {storage.quantity}
                                {storage.total_available_quantity !== undefined && storage.total_available_quantity !== null 
                                  ? ` / ${storage.total_available_quantity}` 
                                  : ''} бр.
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    {editingItem === item.id ? (
                      <div className="edit-actions">
                        <button onClick={() => saveItemEdit(shipment.id, item.id)}>💾</button>
                        <button onClick={cancelEditingItem}>❌</button>
                      </div>
                    ) : (
                      <div className="item-actions">
                        <button onClick={() => startEditingItem(item)} title="Редактирай">✏️</button>
                        <button onClick={() => removeItemFromShipment(shipment.id, item.id)} title="Премахни">🗑️</button>
                        {item.notes && (
                          <button 
                            onClick={() => viewItemNotes(shipment.id, item.id, viewingNotesItem === item.id)}
                            title="Вижте бележки"
                          >
                            📝
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                {editingItem === item.id && (
                  <tr className="notes-row">
                    <td colSpan={6}>
                      <label>
                        Бележки:
                        <textarea
                          value={editForm.notes}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                          className="notes-textarea"
                          rows={3}
                        />
                      </label>
                    </td>
                  </tr>
                )}
                {viewingNotesItem === item.id && item.notes && (
                  <tr className="notes-view-row">
                    <td colSpan={6}>
                      <div className="notes-view">
                        <div className="notes-header">
                          <strong>Бележки:</strong>
                        </div>
                        <div className="notes-text">{item.notes}</div>
                      </div>
                    </td>
                  </tr>
                )}
                {editingNotesItem === item.id && (
                  <tr className="notes-edit-row">
                    <td colSpan={6}>
                      <div className="notes-edit-content">
                        <strong>📝 Редактиране на бележки:</strong>
                        <textarea
                          value={notesEditForm}
                          onChange={(e) => setNotesEditForm(e.target.value)}
                          className="notes-textarea"
                          placeholder="Добавете бележки за този артикул..."
                          rows={4}
                        />
                        <div className="notes-edit-actions">
                          <button 
                            className="action-button save"
                            onClick={() => saveNotesEdit(shipment.id, item.id)}
                          >
                            💾 Запази
                          </button>
                          <button 
                            className="action-button cancel"
                            onClick={cancelEditingNotes}
                          >
                            × Откажи
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SingleShipmentView;
